import { CalendarSlot } from "@/features/booking/BookingCalendar";

/**
 * Cut the spans the wire carries into the discrete slots a calendar can offer (D-40).
 *
 * <p>Since D-91 a span is one of the host's own bookable slots and is already exactly one meeting
 * long, so this normally hands back what it was given. It is kept because the shape of the wire
 * allows a longer span and this is the one place that decides where a meeting may start — a page
 * that assumed one span meant one slot would render a wider one as a single mis-sized offer.</p>
 *
 * <p>Cut from each span's own start on the meeting's own length: a 45-minute ask against a
 * 12:00-16:00 window offers 12:00, 12:45, 13:30 and so on, and stops when what is left will not
 * hold the meeting. Aligning to the clock instead would silently drop the first minutes of every
 * window, which is time the host said was free.</p>
 */
export function slotsFromSpans(
  spans: { start: string; end: string }[],
  durationMinutes: number,
): CalendarSlot[] {
  if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) return [];
  const step = durationMinutes * 60_000;
  const slots: CalendarSlot[] = [];

  for (const span of spans) {
    const from = Date.parse(span.start);
    const until = Date.parse(span.end);
    if (Number.isNaN(from) || Number.isNaN(until)) continue;

    for (let at = from; at + step <= until; at += step) {
      slots.push({
        start: new Date(at).toISOString(),
        end: new Date(at + step).toISOString(),
      });
      // A pathological range (a year of free time, a one-minute meeting) must not lock the tab.
      if (slots.length >= MAX_SLOTS) return slots;
    }
  }
  return slots;
}

const MAX_SLOTS = 500;

/**
 * What the visitor asked for, read out of what they typed.
 *
 * <p>Deliberately small. This resolves a handle and a length so the page knows who to ask and for
 * how long; working out what the request is *about* is the host's classifier's job, and the words
 * are passed through untouched for it. A parser here that decided the purpose would be answering a
 * question only the host's own vocabulary can answer (D-15).</p>
 */
export interface GuestAsk {
  handle: string | null;
  durationMinutes: number;
  /** Their words, whole and unedited, including the handle they typed. */
  notes: string;
}

export function readAsk(text: string, fallbackMinutes = 30): GuestAsk {
  const handle = /@([a-z0-9][a-z0-9._-]{0,38})/i.exec(text);
  const minutes = /(\d{1,3})\s*(?:minutes|minute|mins|min)\b/i.exec(text);
  const hours = /(\d{1,2})\s*(?:hours|hour|hrs|hr)\b/i.exec(text);

  let duration = fallbackMinutes;
  if (minutes) duration = Number(minutes[1]);
  else if (hours) duration = Number(hours[1]) * 60;

  return {
    handle: handle ? handle[1].toLowerCase() : null,
    durationMinutes: duration > 0 && duration <= 8 * 60 ? duration : fallbackMinutes,
    notes: text.trim(),
  };
}

/** Today through a fortnight out, as plain dates. What a first ask reaches for. */
export function defaultRange(now: Date = new Date()): { start: string; end: string } {
  const end = new Date(now.getTime() + 14 * 24 * 60 * 60_000);
  return { start: isoDate(now), end: isoDate(end) };
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}
